import type { Client } from '@/client';
import type { Graphics } from 'pixi.js';

export type WorldEnvironment = {
  kind: 'world_environment'; map_id: number; server_now: number;
  enabled: boolean; outdoors: boolean; game_minute: number;
  day_length_seconds: number; weather: 'clear' | 'rain' | 'snow' | 'fog';
  weather_ends_at: number;
};
type Received = { data: WorldEnvironment; receivedAt: number };
export function worldPeriod(minute: number): 'day' | 'night' { return minute >= 360 && minute < 1080 ? 'day' : 'night'; }
/** Twenty real minutes of dawn/dusk on a four-hour cycle, with eased edges. */
export function worldLighting(minute: number): { night: number; warmth: number } {
  const hour = ((minute % 1440) + 1440) % 1440 / 60;
  const smooth = (t: number) => t * t * (3 - 2 * t);
  if (hour < 4 || hour >= 20) return { night: 1, warmth: 0 };
  if (hour >= 6 && hour < 18) return { night: 0, warmth: 0 };
  const phase = hour < 6 ? (hour - 4) / 2 : (hour - 18) / 2;
  return { night: hour < 6 ? 1 - smooth(phase) : smooth(phase), warmth: Math.sin(Math.PI * phase) };
}
const states = new WeakMap<Client, Received>();

export function clearWorldEnvironment(client: Client): void { states.delete(client); }

export function receiveWorldEnvironment(client: Client, input: unknown): void {
  if (!input || typeof input !== 'object') return;
  const data = input as WorldEnvironment;
  if (data.kind !== 'world_environment' || data.map_id !== client.mapId
    || typeof data.enabled !== 'boolean' || typeof data.outdoors !== 'boolean'
    || !Number.isSafeInteger(data.server_now) || !Number.isSafeInteger(data.weather_ends_at)
    || !Number.isFinite(data.game_minute) || data.game_minute < 0 || data.game_minute >= 1440
    || !Number.isInteger(data.day_length_seconds) || data.day_length_seconds < 600 || data.day_length_seconds > 604800
    || !['clear', 'rain', 'snow', 'fog'].includes(data.weather)) return;
  states.set(client, { data, receivedAt: performance.now() });
}

export function environmentNeedsRefresh(client: Client): boolean {
  const state = states.get(client);
  return !!state && state.data.map_id === client.mapId && state.data.enabled
    && state.data.server_now + performance.now() - state.receivedAt >= state.data.weather_ends_at;
}

export function getWorldEnvironment(client: Client): WorldEnvironment | undefined {
  const state = states.get(client);
  if (!state || state.data.map_id !== client.mapId || performance.now() - state.receivedAt > 120000) return;
  return { ...state.data, game_minute: (state.data.game_minute
    + (performance.now() - state.receivedAt) / (state.data.day_length_seconds * 1000) * 1440) % 1440 };
}

/** Drawn above the world, BELOW names/HUD. Never captures input or affects audio. */
export function drawWorldEnvironment(graphics: Graphics, state: WorldEnvironment, width: number, height: number, time: number, reducedMotion: boolean): void {
  graphics.clear();
  if (!state.enabled || !state.outdoors || width <= 0 || height <= 0) return;
  const light = worldLighting(state.game_minute);
  if (light.night > 0) graphics.rect(0, 0, width, height).fill({ color: 0x111f48, alpha: light.night * 0.32 });
  if (light.warmth > 0) graphics.rect(0, 0, width, height).fill({ color: 0xf1b677, alpha: light.warmth * 0.035 });
  if (state.weather === 'fog') {
    graphics.rect(0, 0, width, height).fill({ color: 0xb8c4cf, alpha: 0.12 });
    return;
  }
  if (state.weather === 'clear') return;
  graphics.rect(0, 0, width, height).fill({ color: 0x233957, alpha: 0.06 });
  if (reducedMotion) return;
  const snow = state.weather === 'snow';
  const count = Math.min(100, Math.ceil(width * height / 10000));
  for (let i = 0; i < count; i++) {
    const speed = snow ? 28 + (i % 7) * 3 : 210 + (i % 7) * 12;
    const y = ((i * 97.31 + time * speed) % (height + 24)) - 12;
    const x = (i * 173.71 + time * (snow ? 9 : -30) + width * 10000) % width;
    if (snow) graphics.circle(x, y, 1 + (i % 2) * 0.5).fill({ color: 0xe3eef7, alpha: 0.6 });
    else graphics.moveTo(x, y).lineTo(x - 2, y + 9).stroke({ color: 0xb0d2ed, alpha: 0.35, width: 1 });
  }
}
